import React from 'react';
import { Edit2, Key, Power, Mail, Phone, Truck, Eye } from 'lucide-react';

/**
 * StaffMemberRow renders a single <tr> that works at every viewport width.
 * All key info fits in one row; actions are always visible as icon buttons.
 */
const StaffMemberRow = ({
    user,
    onEdit,
    onView,
    onResetPassword,
    onToggleEnabled,
}) => {
    const getInitials = (u) => {
        if (u.firstName && u.lastName) return `${u.firstName[0]}${u.lastName[0]}`.toUpperCase();
        return u.username[0].toUpperCase();
    };

    const getAvatarGradient = (username) => {
        const colors = [
            'from-blue-500 to-indigo-600',
            'from-purple-500 to-pink-600',
            'from-emerald-500 to-teal-600',
            'from-orange-500 to-red-600',
            'from-cyan-500 to-blue-600',
        ];
        const index = username.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % colors.length;
        return colors[index];
    };

    const getRoleStyles = (role) => {
        const r = role.toUpperCase();
        if (r.includes('ADMIN'))   return 'bg-purple-50 text-purple-700 border-purple-100';
        if (r.includes('OWNER'))   return 'bg-indigo-50 text-indigo-700 border-indigo-100';
        if (r.includes('MANAGER')) return 'bg-blue-50 text-blue-700 border-blue-100';
        if (r.includes('SALES'))   return 'bg-emerald-50 text-emerald-700 border-emerald-100';
        return 'bg-gray-50 text-gray-700 border-gray-100';
    };

    const visibleRoles = (user.realmRoles || []).filter(
        r => !r.toLowerCase().startsWith('default-roles-yaseero') &&
             !['offline_access', 'uma_authorization'].includes(r)
    );

    const displayName = user.firstName
        ? (user.lastName ? `${user.firstName} ${user.lastName}` : user.firstName)
        : user.username;

    return (
        <tr className="hover:bg-blue-50/30 transition-colors border-b border-gray-100 last:border-0 group">

            {/* ── Avatar + Name + Status ── */}
            <td className="px-3 py-3 w-[30%] min-w-[140px]">
                <div className="flex items-center gap-2.5">
                    <div className={`w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br ${getAvatarGradient(user.username)} flex items-center justify-center text-white font-bold text-sm shadow-sm ring-2 ring-white`}>
                        {getInitials(user)}
                    </div>
                    <div className="min-w-0">
                        <p className="font-bold text-gray-900 text-sm truncate leading-tight">{displayName}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black border ${
                                user.enabled
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                    : 'bg-red-50 text-red-700 border-red-100'
                            }`}>
                                <span className={`w-1 h-1 rounded-full ${user.enabled ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                                {user.enabled ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                            {user.employeeId && (
                                <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">{user.employeeId}</span>
                            )}
                        </div>
                    </div>
                </div>
            </td>

            {/* ── Contact ── */}
            <td className="px-3 py-3 w-[25%] min-w-[140px]">
                <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1 text-[11px] text-gray-600">
                        <Mail size={10} className="text-gray-400 shrink-0" />
                        <span className="truncate max-w-[160px]">{user.email || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-gray-600">
                        <Phone size={10} className="text-gray-400 shrink-0" />
                        <span>{user.phone || '—'}</span>
                    </div>
                    {user.vehicleNumber && (
                        <div className="flex items-center gap-1 text-[11px] font-bold text-gray-700">
                            <Truck size={10} className="text-blue-400 shrink-0" />
                            <span>{user.vehicleNumber}</span>
                        </div>
                    )}
                </div>
            </td>

            {/* ── Roles ── */}
            <td className="px-3 py-3 w-[25%] min-w-[120px]">
                <div className="flex flex-wrap gap-1">
                    {visibleRoles.length > 0 ? (
                        visibleRoles.map(role => (
                            <span
                                key={role}
                                className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider border ${getRoleStyles(role)}`}
                            >
                                {role.replace(/_/g, ' ')}
                            </span>
                        ))
                    ) : (
                        <span className="text-[10px] text-gray-300 font-black uppercase">No Roles</span>
                    )}
                </div>
            </td>

            {/* ── Action Buttons ── always visible ── */}
            <td className="px-3 py-3 w-[20%] min-w-[130px]">
                <div className="flex items-center justify-end gap-0.5">
                    <button
                        onClick={() => onView(user)}
                        className="p-1.5 text-indigo-500 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="View Profile"
                    >
                        <Eye size={15} />
                    </button>
                    <button
                        onClick={() => onEdit(user)}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                    >
                        <Edit2 size={15} />
                    </button>
                    <button
                        onClick={() => onResetPassword(user)}
                        className="p-1.5 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Reset Password"
                    >
                        <Key size={15} />
                    </button>
                    <button
                        onClick={() => onToggleEnabled(user)}
                        className={`p-1.5 rounded-lg transition-colors ${
                            user.enabled ? 'text-red-500 hover:bg-red-50' : 'text-emerald-500 hover:bg-emerald-50'
                        }`}
                        title={user.enabled ? 'Deactivate' : 'Activate'}
                    >
                        <Power size={15} />
                    </button>
                </div>
            </td>
        </tr>
    );
};

export default StaffMemberRow;
